import { Controller } from "@hotwired/stimulus"

// Searchable single-select combobox.
//
// Usage:
//   <div data-controller="searchable-select"
//        data-searchable-select-placeholder-value="Search by name...">
//     <input type="hidden" name="target_user_id" data-searchable-select-target="input">
//     <input type="search" data-searchable-select-target="search"
//            data-action="input->searchable-select#filter
//                         focus->searchable-select#open
//                         keydown->searchable-select#keydown">
//     <ul data-searchable-select-target="list">
//       <li data-searchable-select-target="option" data-value="1"
//           data-label="Ali" data-action="click->searchable-select#pick">Ali</li>
//     </ul>
//   </div>
export default class extends Controller {
  static targets = ["input", "search", "list", "option", "empty"]
  static values = {
    placeholder: { type: String, default: "Search by name..." }
  }

  connect() {
    this._onDocClick = this.onDocumentClick.bind(this)
    document.addEventListener("click", this._onDocClick)
    this.searchTarget.placeholder = this.placeholderValue
    this.syncFromInput()
    this.close()
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick)
  }

  open() {
    this.listTarget.classList.remove("hidden")
    this.filter()
  }

  close() {
    this.listTarget.classList.add("hidden")
  }

  onDocumentClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  filter() {
    const query = this.searchTarget.value.trim().toLowerCase()
    const selectedValue = this.inputTarget.value

    // If the user edits the search text away from the selected label, clear selection.
    if (selectedValue) {
      const selected = this.optionTargets.find((o) => o.dataset.value === selectedValue)
      const selectedLabel = (selected?.dataset.label || "").toLowerCase()
      if (query !== selectedLabel) this.inputTarget.value = ""
    }

    let visible = 0
    const currentValue = this.inputTarget.value

    this.optionTargets.forEach((option) => {
      const label = (option.dataset.label || option.textContent || "").toLowerCase()
      const matches = !query || label.includes(query)
      option.classList.toggle("hidden", !matches)
      option.classList.toggle("bg-primary/10", option.dataset.value === currentValue)
      if (matches) visible += 1
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", visible > 0)
    }

    this.listTarget.classList.remove("hidden")
  }

  pick(event) {
    event.preventDefault()
    const option = event.currentTarget
    this.inputTarget.value = option.dataset.value || ""
    this.searchTarget.value = option.dataset.label || option.textContent.trim()
    this.close()
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  keydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      this.searchTarget.blur()
      return
    }

    if (event.key === "Enter") {
      const firstVisible = this.optionTargets.find((o) => !o.classList.contains("hidden"))
      if (firstVisible) {
        event.preventDefault()
        firstVisible.click()
      }
    }
  }

  clear() {
    this.inputTarget.value = ""
    this.searchTarget.value = ""
    this.filter()
    this.open()
  }

  // Public: used when switching Resident/Vendor radios.
  reset() {
    this.inputTarget.value = ""
    this.searchTarget.value = ""
    this.close()
  }

  syncFromInput() {
    const value = this.inputTarget.value
    if (!value) return
    const match = this.optionTargets.find((o) => o.dataset.value === value)
    if (match) this.searchTarget.value = match.dataset.label || match.textContent.trim()
  }
}
